import React, { useState } from 'react';
import axios from 'axios';

/**
 * Postman Testing Guide Component
 * Provides step-by-step Postman testing instructions for each API service
 */
const PostmanGuide = ({ serviceId }) => {
    const [copiedIndex, setCopiedIndex] = useState(null);

    const copyCode = (code, index) => {
        navigator.clipboard.writeText(code);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const runInPostman = () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

            // Use public endpoint (no auth required) for Postman import
            // Postman's import system works better with public URLs
            const publicCollectionUrl = `${backendUrl}/api/public/postman-collections/${serviceId}`;

            // Use Postman's direct import deep link
            // This should open Postman Desktop and prompt to import the collection
            const deepLink = `postman://import?url=${encodeURIComponent(publicCollectionUrl)}`;

            // Try to open Postman Desktop via deep link
            window.location.href = deepLink;

            // Show helpful message after a short delay
            setTimeout(() => {
                const message =
                    'If Postman Desktop opened:\n' +
                    '• Click "Import" when prompted\n' +
                    '• Update the "api_key" variable with your actual API key\n\n' +
                    'If Postman didn\'t open:\n' +
                    '• Make sure Postman Desktop is installed\n' +
                    '• Or use the Download button and import manually';

                alert(message);
            }, 500);

        } catch (error) {
            console.error('Error opening Postman:', error);
            alert('Failed to open Postman. Please use Download instead.');
        }
    };

    const downloadCollection = async () => {
        try {
            // Use HttpOnly cookie for authentication
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/client/api-services/${serviceId}/postman-collection`,
                {
                    withCredentials: true
                }
            );

            // Create blob from JSON response
            const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${serviceId}_api_collection.json`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Error downloading collection:", error);
            alert("Failed to download collection. It might not be available for this service.");
        }
    };

    // Guide content based on service
    const guides = {
        doqsolv: {
            title: "DoqSolv API - Postman Testing",
            setup: {
                variables: [
                    { name: 'base_url', value: 'https://oms.matrica.in' },
                    { name: 'api_key', value: 'sk_live_YOUR_API_KEY' },
                    { name: 'template_id', value: '' },
                    { name: 'document_id', value: '' },
                    { name: 'order_id', value: '' }
                ]
            },
            tests: [
                {
                    name: "List Templates",
                    method: "GET",
                    endpoint: "{{base_url}}/api/external/v1/doqsolv/templates?scope=global",
                    body: null,
                    description: "List all global templates available for document generation",
                    tip: "Use this to find a `template_id` for use in document generation"
                },
                {
                    name: "Clone Template",
                    method: "POST",
                    endpoint: "{{base_url}}/api/external/v1/doqsolv/templates/clone",
                    body: `{
    "source_template_id": "{{template_id}}",
    "new_name": "My Custom Template"
}`,
                    description: "Copy a global template to your own workspace as a client template",
                    tip: "This is necessary if you wish to modify the template layout on the portal"
                },
                {
                    name: "Generate Document",
                    method: "POST",
                    endpoint: "{{base_url}}/api/external/v1/doqsolv/generate",
                    body: `{
    "template_id": "{{template_id}}",
    "variables": {
        "first_party_name": "John Doe",
        "second_party_name": "Jane Doe"
    },
    "order_id": "{{order_id}}"
}`,
                    description: "Generate a new document PDF from a template by filling in the variables",
                    tip: "Save the `document_id` returned in the response to download the generated PDF. `order_id` is optional but useful for linking to stamp orders."
                },
                {
                    name: "List Generated Documents",
                    method: "GET",
                    endpoint: "{{base_url}}/api/external/v1/doqsolv/documents",
                    body: null,
                    description: "List all documents you have generated previously using DoqSolv"
                },
                {
                    name: "Download Document",
                    method: "GET",
                    endpoint: "{{base_url}}/api/external/v1/doqsolv/documents/{{document_id}}/download?format=pdf",
                    body: null,
                    description: "Download the generated document based on document_id",
                    tip: "Select 'Send and Download' in Postman as this returns a binary file."
                }
            ]
        },
        docu_stamp: {
            title: "Docu Stamp API - Postman Testing",
            setup: {
                variables: [
                    { name: 'base_url', value: 'https://oms.matrica.in' },
                    { name: 'api_key', value: 'sk_live_YOUR_API_KEY' },
                    { name: 'order_id', value: 'YOUR_ORDER_ID' },
                    { name: 'request_id', value: '' },
                    { name: 'document_id', value: '' }
                ]
            },
            tests: [
                {
                    name: "List Available Documents",
                    method: "GET",
                    endpoint: "{{base_url}}/api/external/v1/documents/available?order_id={{order_id}}",
                    body: null,
                    description: "Get list of documents available for an order"
                },
                {
                    name: "Consume Next Document",
                    method: "POST",
                    endpoint: "{{base_url}}/api/external/v1/documents/consume",
                    body: `{
    "request_id": "req_unique_123",
    "order_id": "{{order_id}}"
}`,
                    description: "Consume the next available document. Returns JSON with Base64-encoded file and metadata.",
                    tip: "Save the document_id and request_id from response. The response contains file_base64 which you need to decode. Use a unique request_id - it must be globally unique across all clients."
                },
                {
                    name: "Download Document (Binary)",
                    method: "GET",
                    endpoint: "{{base_url}}/api/external/v1/documents/{{document_id}}/download?request_id={{request_id}}",
                    body: null,
                    description: "Download a document as binary PDF. Requires request_id query parameter. Returns binary file, not JSON.",
                    tip: "Set 'Send and Download' in Postman to save the PDF. This endpoint returns binary data (different from consume which returns JSON)."
                },
                {
                    name: "Get Document by Request ID",
                    method: "GET",
                    endpoint: "{{base_url}}/api/external/v1/documents/by-request/{{request_id}}",
                    body: null,
                    description: "Retrieve a previously consumed document using its request ID. Returns JSON with Base64 content (same structure as consume).",
                    tip: "Use this to retrieve a document you've already consumed. Response includes file_base64 that needs decoding."
                },
                {
                    name: "List All Documents",
                    method: "GET",
                    endpoint: "{{base_url}}/api/external/v1/documents?order_id={{order_id}}&limit=50",
                    body: null,
                    description: "List all documents with pagination. Can filter by status (available/consumed)."
                },
                {
                    name: "Rollback Consumed Documents",
                    method: "POST",
                    endpoint: "{{base_url}}/api/external/v1/documents/rollback",
                    body: `{
    "order_id": "{{order_id}}",
    "request_ids": ["{{request_id}}"],
    "reason": "Document not executed"
}`,
                    description: "Release consumed documents back to available status. Reason must be exactly 'Document not executed'.",
                    tip: "Use this if a document was consumed but not successfully executed. The reason field is strictly validated."
                }
            ]
        },
        esign: {
            title: "eSign API - Postman Testing",
            setup: {
                variables: [
                    { name: 'base_url', value: 'https://oms.matrica.in' },
                    { name: 'api_key', value: 'sk_live_YOUR_API_KEY' },
                    { name: 'envelope_id', value: '' }
                ]
            },
            tests: [
                {
                    name: "Send Document (Single Signer)",
                    method: "POST",
                    endpoint: "{{base_url}}/api/external/esign/send",
                    body: `{
    "signer_name": "Test User",
    "signer_email": "test@example.com",
    "signer_mobile": "9876543210",
    "document_name": "Test_Agreement.pdf",
    "document_data": "JVBERi0xLjQKJeLjz9MK...",
    "reason": "Account Opening",
    "days_to_expire": 7,
    "esign_type": "EKYC",
    "signature_coordinates": [
        {
            "page_number": 1,
            "x_coordinate": 130,
            "y_coordinate": 100,
            "width": 103,
            "height": 50
        }
    ],
    "redirect_url": "https://your-app.com/esign-complete",
    "cancel_redirect_url": "https://your-app.com/esign-cancelled",
    "remove_preview_pdf": false,
    "need_name_match": true,
    "percentage_name_match": 80,
    "need_aadhaar_match": true,
    "aadhaar_number": "1234",
    "need_gender_match": true,
    "gender": "M",
    "debit": false
}`,
                    description: "Send a document to a single signer. Save the envelope_id from response!",
                    tip: "Replace document_data with your actual Base64-encoded PDF"
                },
                {
                    name: "Check Status",
                    method: "GET",
                    endpoint: "{{base_url}}/api/external/esign/status/{{envelope_id}}",
                    body: null,
                    description: "Check the current status of an envelope"
                },
                {
                    name: "List Envelopes",
                    method: "GET",
                    endpoint: "{{base_url}}/api/external/esign/envelopes?limit=10",
                    body: null,
                    description: "List all your envelopes with pagination"
                },
                {
                    name: "Download Signed Document",
                    method: "GET",
                    endpoint: "{{base_url}}/api/external/esign/download/{{envelope_id}}",
                    body: null,
                    description: "Download signed PDF (only works after status is 'signed')",
                    tip: "Set 'Send and Download' in Postman to save the PDF response"
                },
                {
                    name: "Multi-Signer Document",
                    method: "POST",
                    endpoint: "{{base_url}}/api/external/esign/multi-send",
                    body: `{
    "document_name": "Partnership.pdf",
    "document_data": "JVBERi0xLjQK...",
    "days_to_expire": 7,
    "signers": [
        {
            "signer_id": "partner-1",
            "name": "First Partner",
            "email": "first@example.com",
            "mobile": "9876543210",
            "sequence": 1,
            "signature_coordinates": [
                {"page_number": 1, "x_coordinate": 100, "y_coordinate": 200}
            ]
        },
        {
            "signer_id": "partner-2",
            "name": "Second Partner",
            "email": "second@example.com",
            "mobile": "9123456789",
            "sequence": 2,
            "signature_coordinates": [
                {"page_number": 1, "x_coordinate": 300, "y_coordinate": 200}
            ]
        }
    ]
}`,
                    description: "Send document requiring multiple signers in sequence"
                }
            ]
        },
        digisign: {
            title: "Digital Signature API - Postman Testing",
            setup: {
                variables: [
                    { name: 'base_url', value: 'https://oms.matrica.in' },
                    { name: 'api_key', value: 'sk_live_YOUR_API_KEY' },
                    { name: 'document_id', value: '' }
                ]
            },
            tests: [
                {
                    name: "Initiate DigiSign (Email Mode)",
                    method: "POST",
                    endpoint: "{{base_url}}/api/external/digisign/initiate",
                    body: `{
    "signature_mode": "email",
    "document_name": "Agreement.pdf",
    "document_data": "JVBERi0xLjQK...",
    "recipients": [
        {
            "name": "John Doe",
            "email": "john@example.com",
            "signature_coordinate": {
                "page_number": 1,
                "x_coordinate": 100,
                "y_coordinate": 200,
                "width": 100,
                "height": 50
            }
        }
    ]
}`,
                    description: "Initiate signature via email notification to the recipient.",
                    tip: "Save the document_id from the response to check status later."
                },
                {
                    name: "Initiate DigiSign (URL Mode)",
                    method: "POST",
                    endpoint: "{{base_url}}/api/external/digisign/initiate",
                    body: `{
    "signature_mode": "signing_url",
    "document_name": "Contract.pdf",
    "document_data": "JVBERi0xLjQK...",
    "recipients": [
        {
            "name": "Jane Doe",
            "email": "jane@example.com",
            "signature_coordinate": {
                "page_number": 1,
                "x_coordinate": 150,
                "y_coordinate": 250
            }
        }
    ],
    "redirect_url": "https://your-app.com/callback"
}`,
                    description: "Get a signing URL to embed or redirect the user immediately.",
                    tip: "The response will contain signing_urls array with the link."
                }
            ]
        },
        bulk_order_management: {
            title: "Bulk Order Management API - Postman Testing",
            setup: {
                variables: [
                    { name: 'base_url', value: 'https://oms.matrica.in' },
                    { name: 'api_key', value: 'sk_live_YOUR_API_KEY' },
                    { name: 'order_id', value: '' },
                    { name: 'batch_id', value: '' },
                    { name: 'external_ref_id', value: '' }
                ]
            },
            tests: [
                {
                    name: "Create Bulk Orders",
                    method: "POST",
                    endpoint: "{{base_url}}/api/external/v2/orders/bulk",
                    body: `{
    "batch_reference": "MY-BATCH-001",
    "orders": [
        {
            "state": "Maharashtra",
            "article": "Sale Agreement",
            "quantity": 50,
            "denomination": 100,
            "first_party": {
                "name": "Rahul Sharma",
                "mobile": "9876543210",
                "address": "123 Main St, Mumbai"
            },
            "second_party": {
                "name": "Priya Industries Pvt Ltd",
                "mobile": "9123456789",
                "address": "456 Commercial Complex, Pune"
            },
            "consideration_amount": 5000000,
            "delivery_mode": "digital",
            "external_ref_id": "YOUR-REF-001"
        },
        {
            "state": "Gujarat",
            "article": "Lease Deed",
            "quantity": 25,
            "denomination": 200,
            "first_party": {
                "name": "ABC Corporation",
                "mobile": "7890123456"
            },
            "delivery_mode": "physical",
            "delivery_address": "789 Industrial Area, Ahmedabad 380001",
            "external_ref_id": "YOUR-REF-002"
        }
    ]
}`,
                    description: "Create up to 500 orders in a single request. Save the batch_id and order_ids from response!",
                    tip: "Mobile: 10 digits starting with 6-9. Quantity: 10-10000. Denomination: 10-500 (whole numbers only)."
                },
                {
                    name: "Check Bulk Order Status",
                    method: "POST",
                    endpoint: "{{base_url}}/api/external/v2/orders/status/bulk",
                    body: `{
    "order_ids": [
        "{{order_id}}"
    ]
}`,
                    description: "Check status of multiple orders at once (up to 500 order IDs).",
                    tip: "Replace with actual order_ids from the create response. Returns status, created_at, updated_at for each."
                },
                {
                    name: "Get Single Order Details",
                    method: "GET",
                    endpoint: "{{base_url}}/api/external/v2/orders/{{order_id}}",
                    body: null,
                    description: "Get detailed information for a specific order including stamp duty calculations.",
                    tip: "Returns total_stamp_duty (qty × denomination), paid_stamp_duty, and remaining_stamp_duty."
                },
                {
                    name: "Cancel Order",
                    method: "POST",
                    endpoint: "{{base_url}}/api/external/v2/orders/{{order_id}}/cancel?cancellation_reason=Customer%20requested%20cancellation",
                    body: null,
                    description: "Cancel an order that hasn't been processed yet.",
                    tip: "Only works for orders in: placed, assigned, sample_submitted, sample_rejected status. Reason: 5-500 chars."
                }
            ]
        },
        live_stamping: {
            title: "Live Stamping API - Postman Testing",
            setup: {
                variables: [
                    { name: 'base_url', value: 'https://oms.matrica.in' },
                    { name: 'api_key', value: 'sk_live_YOUR_API_KEY' },
                    { name: 'service_id', value: 'YOUR_SERVICE_ID' },
                    { name: 'order_id', value: '' }
                ]
            },
            tests: [
                {
                    name: "List Services",
                    method: "GET",
                    endpoint: "{{base_url}}/api/external/v1/live-stamping/services",
                    body: null,
                    description: "List all enabled Live Stamping services.",
                    tip: "Use this to get the `service_id` required for placing an order."
                },
                {
                    name: "Place Order",
                    method: "POST",
                    endpoint: "{{base_url}}/api/external/v1/live-stamping/request",
                    body: `{
    "service_id": "{{service_id}}",
    "first_party": {
        "name": "John Doe",
        "mobile": "9876543210"
    },
    "second_party": {
        "name": "Jane Smith",
        "mobile": "9123456789"
    },
    "article": "Affidavit",
    "denomination": 100,
    \"quantity\": 1,
    "state": "Maharashtra",
    "purchased_paid_by": "first_party",
    "delivery_mode": "digital",
    "external_ref_id": "MY-REF-001"
}`,
                    description: "Place a new Live Stamping order. Wallet will be debited.",
                    tip: "Ensure `service_id` is valid. Returns `order_id` which you should save."
                },
                {
                    name: "Get Order Details",
                    method: "GET",
                    endpoint: "{{base_url}}/api/external/v1/live-stamping/orders/{{order_id}}",
                    body: null,
                    description: "Get status and details of a specific order.",
                    tip: "Check the `status` field provided in the response."
                },
                {
                    name: "Get Wallet Balance",
                    method: "GET",
                    endpoint: "{{base_url}}/api/external/v1/live-stamping/wallet",
                    body: null,
                    description: "Check your current wallet balance.",
                    tip: "Returns current balance in INR."
                }
            ]
        }
    };

    const guide = guides[serviceId] || guides.esign;

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="mb-6 flex items-start justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        🧪 {guide.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        Test API endpoints quickly using Postman
                    </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={downloadCollection}
                            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium shadow-sm"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download
                        </button>
                        <button
                            onClick={runInPostman}
                            className="flex items-center gap-2 px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm font-medium shadow-sm"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Run in Postman
                        </button>
                    </div>
                    <span className="text-xs text-gray-500 text-right max-w-xs">
                        Opens Postman Desktop to import collection. Update the API key variable after import.
                    </span>
                </div>
            </div>

            {/* Setup Section */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-orange-800 mb-3 flex items-center gap-2">
                    ⚙️ Postman Setup
                </h4>
                <div className="space-y-3">
                    <div className="text-sm text-gray-700">
                        <p className="mb-2"><strong>1.</strong> Create a new Collection in Postman</p>
                        <p className="mb-2"><strong>2.</strong> Set Authorization to <code className="bg-orange-100 px-1 rounded">API Key</code> with:</p>
                        <ul className="list-disc list-inside ml-4 text-gray-600">
                            <li>Key: <code className="bg-gray-100 px-1 rounded">X-API-Key</code></li>
                            <li>Value: <code className="bg-gray-100 px-1 rounded">{"{{api_key}}"}</code></li>
                            <li>Add to: <code className="bg-gray-100 px-1 rounded">Header</code></li>
                        </ul>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-700 mb-2"><strong>3.</strong> Create Environment Variables:</p>
                        <div className="bg-white border rounded overflow-hidden">
                            <table className="min-w-full text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Variable</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Value</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {guide.setup.variables.map((v, i) => (
                                        <tr key={i}>
                                            <td className="px-3 py-2 font-mono text-indigo-600">{v.name}</td>
                                            <td className="px-3 py-2 text-gray-600">{v.value || <em className="text-gray-400">Set after first test</em>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Test Cases */}
            <h4 className="font-medium text-gray-900 mb-3">📋 Test Cases</h4>
            <div className="space-y-4">
                {guide.tests.map((test, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${test.method === 'GET' ? 'bg-green-100 text-green-700' :
                                    test.method === 'POST' ? 'bg-blue-100 text-blue-700' :
                                        'bg-gray-100 text-gray-700'
                                    }`}>
                                    {test.method}
                                </span>
                                <span className="font-medium text-gray-900">{test.name}</span>
                            </div>
                        </div>
                        <div className="p-4 space-y-3">
                            <p className="text-sm text-gray-600">{test.description}</p>

                            {test.tip && (
                                <div className="bg-blue-50 border border-blue-200 rounded p-2 text-sm text-blue-700 flex items-start gap-2">
                                    <span>💡</span>
                                    <span>{test.tip}</span>
                                </div>
                            )}

                            <div>
                                <p className="text-xs text-gray-500 mb-1">URL:</p>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 bg-gray-900 text-green-400 px-3 py-2 rounded text-sm font-mono overflow-x-auto">
                                        {test.endpoint}
                                    </code>
                                    <button
                                        onClick={() => copyCode(test.endpoint, `url-${index}`)}
                                        className={`px-2 py-1 rounded text-xs ${copiedIndex === `url-${index}` ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                            }`}
                                    >
                                        {copiedIndex === `url-${index}` ? '✓' : 'Copy'}
                                    </button>
                                </div>
                            </div>

                            {test.body && (
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Body (JSON):</p>
                                    <div className="relative">
                                        <button
                                            onClick={() => copyCode(test.body, `body-${index}`)}
                                            className={`absolute top-2 right-2 px-2 py-1 rounded text-xs ${copiedIndex === `body-${index}` ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                }`}
                                        >
                                            {copiedIndex === `body-${index}` ? '✓ Copied!' : 'Copy'}
                                        </button>
                                        <pre className="bg-gray-900 text-gray-100 p-3 rounded text-sm overflow-x-auto max-h-64">
                                            <code>{test.body}</code>
                                        </pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Quick Tips */}
            <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">💡 Quick Tips</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                    {serviceId === 'docu_stamp' ? (
                        <>
                            <li>• Save <code className="bg-gray-200 px-1 rounded">document_id</code> and <code className="bg-gray-200 px-1 rounded">request_id</code> from consume response</li>
                            <li>• Consume endpoint returns JSON with <code className="bg-gray-200 px-1 rounded">file_base64</code> - decode it to get the PDF</li>
                            <li>• Download endpoint returns binary PDF directly (use "Send and Download" in Postman)</li>
                            <li>• <code className="bg-gray-200 px-1 rounded">request_id</code> must be globally unique across all clients</li>
                            <li>• Use same <code className="bg-gray-200 px-1 rounded">request_id</code> for idempotency (returns same document)</li>
                            <li>• Check response status codes: 200=Success, 401=Auth Error, 404=No Documents, 409=Request ID Conflict</li>
                        </>
                    ) : serviceId === 'bulk_order_management' ? (
                        <>
                            <li>• Save <code className="bg-gray-200 px-1 rounded">batch_id</code> and <code className="bg-gray-200 px-1 rounded">order_id</code> from create response</li>
                            <li>• Mobile numbers must be 10 digits starting with 6, 7, 8, or 9 (or +91 prefix)</li>
                            <li>• Quantity range: Min 1, No Max. Denomination: Min 1, No Max.</li>
                            <li>• Use <code className="bg-gray-200 px-1 rounded">external_ref_id</code> to track orders with your own reference</li>
                            <li>• <code className="bg-gray-200 px-1 rounded">purchased_paid_by</code> accepts any custom text (e.g. "Vendor Name")</li>
                            <li>• For physical delivery, <code className="bg-gray-200 px-1 rounded">delivery_address</code> is required</li>
                            <li>• Cancellation only works for: placed, assigned, sample_submitted, sample_rejected statuses</li>
                        </>
                    ) : (
                        <>
                            <li>• Save <code className="bg-gray-200 px-1 rounded">envelope_id</code> from send response to use in other tests</li>
                            <li>• Set Content-Type header to <code className="bg-gray-200 px-1 rounded">application/json</code> for POST requests</li>
                            <li>• Use "Send and Download" for download endpoints to save PDF files</li>
                            <li>• Check response status codes: 200=Success, 401=Auth Error, 403=Not Enabled</li>
                        </>
                    )}
                </ul>
            </div>
        </div>
    );
};

export default PostmanGuide;
